import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldWarning, ArrowsClockwise, Database, Trash, Fire, Star,
} from "@phosphor-icons/react";

/**
 * AdminCelebrityCachePage
 *
 * Super-admin control panel for the Celebrity Database cache. See row
 * counts, run the warmer (pre-fill for the entire library), invalidate
 * per-slug or across-the-board. Base analysis is free (Swiss-Ephemeris
 * math, no LLM credits) — this page just makes it faster.
 */

function fmtInt(n) { if (n == null) return "—"; return Number(n).toLocaleString("en-IN"); }
function fmtBytes(b) { if (!b) return "0 B"; if (b < 1024) return `${b} B`; if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`; return `${(b/1024/1024).toFixed(2)} MB`; }

export default function AdminCelebrityCachePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warming, setWarming] = useState(false);
  const [warmResult, setWarmResult] = useState(null);
  const [invalidating, setInvalidating] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [kindInput, setKindInput] = useState("all");
  const [includeOg, setIncludeOg] = useState(true);
  const [error, setError] = useState("");

  const isSA = !!user?.is_super_admin;

  const refreshStats = useCallback(async () => {
    if (!isSA) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/celebrities/cache/stats");
      setStats(data);
    } catch (err) {
      const m = err?.response?.data?.detail || err?.message || "Failed to load stats";
      setError(m); toast.error(m);
    } finally { setLoading(false); }
  }, [isSA]);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  const runWarm = async () => {
    setWarming(true); setWarmResult(null);
    try {
      const { data } = await api.post("/admin/celebrities/cache/warm", null, {
        params: { include_og: includeOg },
        timeout: 240000,
      });
      setWarmResult(data);
      toast.success(`Warmed ${data.detail_cached}/${data.celebrities_processed} celebs in ${(data.elapsed_ms/1000).toFixed(1)}s`);
      refreshStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Warm failed");
    } finally { setWarming(false); }
  };

  const runInvalidate = async () => {
    setInvalidating(true);
    try {
      const body = { kind: kindInput };
      if (slugInput.trim()) body.slug = slugInput.trim();
      const { data } = await api.post("/admin/celebrities/cache/invalidate", body);
      const totalDeleted = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Invalidated ${totalDeleted} row${totalDeleted === 1 ? "" : "s"}`);
      refreshStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Invalidate failed");
    } finally { setInvalidating(false); }
  };

  if (!isSA) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="celeb-cache-denied">
          <ShieldWarning size={48} weight="duotone" className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif-display text-stone-800 mb-2">Super-admin only</h1>
          <p className="text-stone-600">This panel manages the Celebrity Database cache.</p>
          <button onClick={() => navigate("/")} className="mt-6 px-5 py-2 rounded-full bg-stone-800 text-white hover:bg-stone-900" data-testid="back-home">Back to home</button>
        </div>
        <Footer />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header />
      <main className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-celeb-cache-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Super Admin</div>
            <h1 className="text-3xl sm:text-4xl font-serif-display text-stone-800 flex items-center gap-2">
              <Star size={26} weight="fill" className="text-amber-500" /> Celebrity Database — Cache
            </h1>
            <p className="text-stone-600 mt-1 text-sm max-w-2xl">
              The base kundali analysis + Varshaphal + OG images are all deterministic Swiss-Ephemeris output. Pre-compute once, serve from Mongo forever. Ask-AI still uses the LLM per-question (that&apos;s the only credit-consuming path).
            </p>
          </div>
          <button onClick={refreshStats} disabled={loading}
            className="rounded-full bg-stone-800 text-white text-sm px-4 py-1.5 inline-flex items-center gap-1.5 hover:bg-stone-900 disabled:opacity-60"
            data-testid="refresh-btn">
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm">{error}</div>
        )}

        {/* Stats cards */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cache-stats">
          <StatCard label="Detail rows" icon={Database} value={fmtInt(stats?.detail_rows)} sub="chart analyses cached" />
          <StatCard label="Varshaphal rows" icon={Database} value={fmtInt(stats?.varshaphal_rows)} sub="slug × year keys" />
          <StatCard label="OG images" icon={Database} value={fmtInt(stats?.og_rows)} sub="1200×630 PNGs" />
          <StatCard label="OG storage" icon={Database} value={fmtBytes(stats?.og_total_bytes)} sub="total on disk" />
        </div>

        {/* Warm panel */}
        <section className="mt-8 rounded-2xl border border-amber-300 bg-amber-50/60 p-5" data-testid="warm-panel">
          <h2 className="text-xl font-serif-display text-stone-800 flex items-center gap-2 mb-2">
            <Fire size={20} weight="fill" className="text-amber-600" /> Warm the cache
          </h2>
          <p className="text-sm text-stone-700 max-w-2xl">
            Pre-fills the detail (and optionally OG) cache for every celebrity in the catalogue. Run once after deploy so the first user-visit is instant. Safe to run repeatedly — already-cached entries are no-ops.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" checked={includeOg} onChange={(e) => setIncludeOg(e.target.checked)} data-testid="warm-include-og" />
              Also warm OG images
            </label>
            <button onClick={runWarm} disabled={warming}
              className="rounded-full bg-amber-700 hover:bg-amber-800 text-white text-sm px-4 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="warm-btn">
              {warming ? <ArrowsClockwise size={14} className="animate-spin" /> : <Fire size={14} weight="fill" />}
              {warming ? "Warming…" : "Warm now"}
            </button>
          </div>
          {warmResult && (
            <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 text-sm" data-testid="warm-result">
              <div>Processed: <span className="font-semibold">{warmResult.celebrities_processed}</span> celebs in {(warmResult.elapsed_ms / 1000).toFixed(1)}s</div>
              <div>Detail cached: <span className="text-emerald-700">{warmResult.detail_cached}</span> · OG cached: <span className="text-emerald-700">{warmResult.og_cached}</span> · Errors: <span className={warmResult.errors ? "text-rose-700" : "text-stone-500"}>{warmResult.errors}</span></div>
            </div>
          )}
        </section>

        {/* Invalidate panel */}
        <section className="mt-6 rounded-2xl border border-rose-300 bg-rose-50/60 p-5" data-testid="invalidate-panel">
          <h2 className="text-xl font-serif-display text-stone-800 flex items-center gap-2 mb-2">
            <Trash size={20} weight="fill" className="text-rose-600" /> Invalidate
          </h2>
          <p className="text-sm text-stone-700 max-w-2xl">
            Drop cache rows so the next request re-computes and re-stores. Use after you edit a celeb&apos;s birth time / slug in <code>celebrities.py</code>. Auto-invalidation via fingerprint also kicks in — this is just for a manual nudge.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">Slug (empty = all)</label>
              <input type="text" value={slugInput} onChange={(e) => setSlugInput(e.target.value)}
                placeholder="e.g. adani-gautam"
                className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm"
                data-testid="invalidate-slug" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">Kind</label>
              <select value={kindInput} onChange={(e) => setKindInput(e.target.value)}
                className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm"
                data-testid="invalidate-kind">
                <option value="all">All (detail + varshaphal + og)</option>
                <option value="detail">Detail only</option>
                <option value="varshaphal">Varshaphal only</option>
                <option value="og">OG images only</option>
              </select>
            </div>
            <button onClick={runInvalidate} disabled={invalidating}
              className="rounded-full bg-rose-700 hover:bg-rose-800 text-white text-sm px-4 py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              data-testid="invalidate-btn">
              {invalidating ? <ArrowsClockwise size={14} className="animate-spin" /> : <Trash size={14} weight="fill" />}
              Invalidate
            </button>
          </div>
        </section>

        <div className="mt-6 text-[11px] text-stone-500" data-testid="disclaimer">
          Base celebrity analysis and OG images use zero Universal Key credits — they&apos;re pure Swiss-Ephemeris math. The cache here is a CPU/latency optimisation, not a credit-saver.
        </div>
      </main>
      <Footer />
    </PageShell>
  );
}

function StatCard({ label, icon: Icon, value, sub }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
        {Icon && <Icon size={11} weight="fill" />} {label}
      </div>
      <div className="font-serif-display text-2xl mt-0.5 text-stone-900">{value}</div>
      {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}
